import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Tag, Tooltip } from 'antd';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import {
  CREATING,
  HISTORICAL_SCENARIO,
  NEVER_AUTO_TRADED,
  playPauseSession,
  selectors,
  Session,
} from 'state';
import { isSessionLocked } from '../helpers';
import { useSettingsLockNudge } from '../SettingsLockNudge';

interface TopBarProps {
  session: Session;
  klassName?: string;
  // Blueprint authoring reuses this settings shell against a template session
  // (always CREATING, never runnable) — so it hides the "start session" button.
  // Trainer/in-session usage omits this, keeping the button exactly as before.
  hideStartButton?: boolean;
  /** Optional live pre-start instrument draft state from Dense settings. */
  selectedAssetCount?: number;
  instrumentDraftReady?: boolean;
  instrumentDraftDirty?: boolean;
  instrumentDraftSaving?: boolean;
  instrumentDraftError?: string | null;
  instrumentLimitLaunchReady?: boolean;
}

const STATUS_LABELS: { [k: number]: string } = {
  0: 'Paused',
  1: 'Playing',
  2: 'Finished',
  3: 'Creating',
  4: 'Delay',
  5: 'Frozen',
};

const STATUS_COLORS: { [k: number]: string } = {
  0: 'orange',
  1: 'green',
  2: 'default',
  3: 'blue',
  4: 'gold',
  5: 'cyan',
};

/** Context command bar — mirrors the Analysis Studio TopBar (brand + a
 *  middot-separated meta line) and adds an editability chip on the right. */
const TopBar: React.FC<TopBarProps> = ({
  session,
  klassName,
  hideStartButton,
  selectedAssetCount,
  instrumentDraftReady = true,
  instrumentDraftDirty = false,
  instrumentDraftSaving = false,
  instrumentDraftError,
  instrumentLimitLaunchReady = true,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currencies = useSelector(selectors.currencies);
  const currencySymbol = currencies?.[session.reporting_currency]?.symbol ?? '';
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  const locked = isSessionLocked(session);
  // Pulses the lock pill each time a locked settings surface is clicked (the
  // changing token is used as the pill's key to restart the CSS animation).
  const { token: lockNudgeToken } = useSettingsLockNudge();

  // "Lock settings & start session" — shown only while the session is still
  // being created. Wired exactly like Overview's "Finish Creating" button
  // (playPauseSession with reload-on-creating + the same guard conditions).
  const isCreating = session.status === CREATING;
  const waiting = useSelector(selectors.waiting);
  const noAssets =
    (selectedAssetCount ?? session.underlying_assets.length) === 0;
  const instrumentDraftBlocked =
    !instrumentDraftReady ||
    instrumentDraftDirty ||
    instrumentDraftSaving ||
    Boolean(instrumentDraftError) ||
    !instrumentLimitLaunchReady;
  const startDisabled =
    noAssets ||
    instrumentDraftBlocked ||
    session.auto_trader_status !== NEVER_AUTO_TRADED ||
    session.currently_passing_time;
  const startTooltip = noAssets
    ? 'Select instruments before starting the session.'
    : !instrumentDraftReady
      ? 'Loading the saved instrument-limit draft.'
      : instrumentDraftError
        ? 'The latest instrument settings could not be saved. Retry before starting.'
        : instrumentDraftDirty || instrumentDraftSaving
          ? 'Saving the latest instrument settings before launch.'
          : !instrumentLimitLaunchReady
            ? 'Complete every required V2 notional limit before starting the session.'
            : '';

  return (
    <div className="set-studio__topbar">
      <div className="set-studio__brand">
        <Tooltip title={klassName}>
          <span className="set-studio__brand-title">
            {klassName ?? 'Session'} — Settings
          </span>
        </Tooltip>
        <span className="set-studio__brand-meta">
          <Tag
            color={STATUS_COLORS[session.status] ?? 'default'}
            style={{ marginInlineEnd: 0 }}
          >
            {STATUS_LABELS[session.status] ?? 'Unknown'}
          </Tag>
          <span>·</span>
          <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
            {isHistorical ? 'Historical' : 'Real Time'}
          </Tag>
          {currencySymbol ? (
            <>
              <span>·</span>
              {currencySymbol}
            </>
          ) : null}
        </span>
      </div>

      <div className="set-studio__topbar-right">
        {isCreating && !hideStartButton && (
          <Tooltip title={startTooltip}>
            <span>
              <Button
                className="set-studio__startbtn"
                icon={<UnlockOutlined />}
                loading={waiting}
                disabled={startDisabled}
                onClick={() => {
                  dispatch(
                    playPauseSession({
                      id: session.id,
                      current_status: session.status,
                      reload: session.status === CREATING,
                    }),
                  );
                  // Starting leaves the Settings tab behind — send the trainer
                  // to Overview (the reload above then refreshes it there).
                  history.push(`/session/${session.id}/overview`);
                }}
              >
                Lock settings &amp; start session
              </Button>
            </span>
          </Tooltip>
        )}
        {/* Editability chip: shown only once the session has started (status
            !== creating) to explain why some settings are locked. In creating
            mode everything is editable, so the chip is hidden entirely. */}
        {locked && (
          <Tooltip title="The session has started — structural settings (instruments, hard/soft limits, margins, futures, per-sector alpha, benchmark) are locked. Behavioural settings remain editable.">
            <span
              key={lockNudgeToken}
              className={`set-studio__lockchip set-studio__lockchip--locked${
                lockNudgeToken ? ' set-studio__lockchip--nudge' : ''
              }`}
            >
              <LockOutlined />
              Structural settings locked
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default TopBar;
